import { useEffect, useMemo, useState } from "react";
import { TableCellInputType } from "@components/data-table/types";
import { useGetAggregateAssets } from "@hooks/use-get/aggregate-assets";
import { getDistinctAggregateValues } from "@hooks/use-get/aggregate-assets/utils";
import type { ConfigDrivenReportConfig } from "@hooks/use-reports/types";
import {
  mapDbTableToDataSource,
  useDataSourceConfig,
} from "@hooks/use-reports/shared/data-source-config";
import { underscoreToTitleCase } from "@utils/index";
import {
  getColumnMetadata,
  type ColumnMetadata,
} from "../add-stat-card-modal/utils";

type FilterOption = { label: string; value: string | number };

interface UseReportFieldOptionsArgs {
  reportConfig: ConfigDrivenReportConfig;
  filterColumn: string;
  useFilter: boolean;
}

export const useReportFieldOptions = ({
  reportConfig,
  filterColumn,
  useFilter,
}: UseReportFieldOptionsArgs) => {
  const [dynamicFilterOptions, setDynamicFilterOptions] = useState<FilterOption[]>([]);
  const [dynamicFilterLoading, setDynamicFilterLoading] = useState(false);
  const { getAggregateAssets } = useGetAggregateAssets();

  const primaryDataSource = reportConfig.dataSources?.[0];
  const dataSourceConfig = useDataSourceConfig(
    mapDbTableToDataSource(primaryDataSource?.dbTable)
  );

  const availableColumns = useMemo((): ColumnMetadata[] => {
    if (!primaryDataSource?.groupBy?.length) {
      return [];
    }

    const groupByColumns = primaryDataSource.groupBy.map((fieldName) => {
      const fieldConfig = dataSourceConfig.fieldConfigs?.[fieldName];
      return getColumnMetadata(
        fieldName,
        fieldConfig?.name || underscoreToTitleCase(fieldName),
        fieldConfig
      );
    });

    const groupByIds = new Set(primaryDataSource.groupBy);
    const sumColumns = (primaryDataSource.sum ?? [])
      .filter((fieldName) => !groupByIds.has(fieldName))
      .map((fieldName) => {
        const fieldConfig = dataSourceConfig.fieldConfigs?.[fieldName];
        const baseColumn = getColumnMetadata(
          fieldName,
          fieldConfig?.name || underscoreToTitleCase(fieldName),
          fieldConfig
        );

        return {
          ...baseColumn,
          isNumeric: true,
        };
      });

    return [...groupByColumns, ...sumColumns].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [dataSourceConfig.fieldConfigs, primaryDataSource]);

  const availableDateColumns = useMemo(() => {
    return (dataSourceConfig.tableColumns as any[])
      .filter(
        (column) =>
          column.inputType === TableCellInputType.Date ||
          column.inputType === TableCellInputType.DateTime
      )
      .map((column) => ({ id: column.id, label: column.label }))
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label)
      );
  }, [dataSourceConfig.tableColumns]);

  // Filter columns available for filter selection (exclude date/datetime columns)
  const filterableColumns = useMemo(() => {
    const dateColumnIds = new Set(availableDateColumns.map((col) => col.id));
    return availableColumns.filter((col) => !dateColumnIds.has(col.id));
  }, [availableColumns, availableDateColumns]);

  useEffect(() => {
    if (!useFilter || !filterColumn) {
      setDynamicFilterOptions([]);
      setDynamicFilterLoading(false);
      return;
    }

    const selectedColumn = availableColumns.find((column) => column.id === filterColumn);
    if (!selectedColumn || selectedColumn.hasOptions) {
      setDynamicFilterOptions([]);
      setDynamicFilterLoading(false);
      return;
    }

    let isCancelled = false;
    setDynamicFilterLoading(true);

    getAggregateAssets({ groupBy: [filterColumn] })
      .then((result: { data: Record<string, string | number>[] }) => {
        if (isCancelled) {
          return;
        }

        const values = getDistinctAggregateValues(result.data, filterColumn);

        setDynamicFilterOptions(values.map((value) => ({ label: value, value })));
      })
      .finally(() => {
        if (!isCancelled) {
          setDynamicFilterLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [availableColumns, filterColumn, getAggregateAssets, useFilter]);

  const filterColumnOptions = useMemo(() => {
    if (!filterColumn) {
      return [] as FilterOption[];
    }

    const selectedColumn = availableColumns.find((column) => column.id === filterColumn);
    return selectedColumn?.hasOptions
      ? selectedColumn.options || []
      : dynamicFilterOptions;
  }, [availableColumns, dynamicFilterOptions, filterColumn]);

  return {
    availableColumns,
    availableDateColumns,
    filterableColumns,
    filterColumnOptions,
    dynamicFilterLoading,
  };
};